import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { X, CheckCircle, ArrowRight } from "@phosphor-icons/react";

export default function RequestDemoModal({ open, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", club_name: "", role: "head_coach", message: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ name: "", email: "", club_name: "", role: "head_coach", message: "" });
      setErr("");
      setSent(false);
      setBusy(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const setField = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: k === "email" ? e.target.value.replace(/\s+/g, "") : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await api.post("/contact/demo-request", form);
      setSent(true);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      data-testid="demo-modal"
    >
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={() => onClose?.()}
        aria-hidden="true"
      />
      <div className="surface relative w-full max-w-md p-6" data-testid="demo-modal-card">
        <button
          onClick={() => onClose?.()}
          className="absolute top-3 right-3 btn-ghost"
          aria-label="Close"
          data-testid="demo-modal-close"
        >
          <X size={14} weight="bold" />
        </button>

        {sent ? (
          <div className="text-center py-4" data-testid="demo-success">
            <CheckCircle size={40} weight="duotone" className="text-[#16ec06] mx-auto mb-3" />
            <h2 className="font-heading uppercase tracking-tight text-2xl mb-2">Thanks — we got it</h2>
            <p className="text-zinc-400 text-sm">
              We'll review your request and get back to you within 48 hours. Keep an eye on your inbox.
            </p>
            <button onClick={() => onClose?.()} className="btn-primary mt-6" data-testid="demo-success-close">
              Close
            </button>
          </div>
        ) : (
          <>
            <div className="font-label mb-1">Bring your club to courtsideOS</div>
            <h2 className="font-heading uppercase tracking-tight text-2xl mb-4">Request a demo</h2>
            <form onSubmit={submit} className="space-y-3" data-testid="demo-form">
              <div>
                <label className="font-label block mb-1">Your name</label>
                <input
                  className="input"
                  required
                  value={form.name}
                  onChange={setField("name")}
                  placeholder="Alex Coach"
                  data-testid="demo-name"
                  autoFocus
                />
              </div>
              <div>
                <label className="font-label block mb-1">Email</label>
                <input
                  className="input"
                  type="email"
                  required
                  value={form.email}
                  onChange={setField("email")}
                  placeholder="alex@yourclub.co.uk"
                  data-testid="demo-email"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-label block mb-1">Club</label>
                  <input
                    className="input"
                    value={form.club_name}
                    onChange={setField("club_name")}
                    placeholder="Your club"
                    data-testid="demo-club"
                  />
                </div>
                <div>
                  <label className="font-label block mb-1">Your role</label>
                  <select
                    className="input"
                    value={form.role}
                    onChange={setField("role")}
                    data-testid="demo-role"
                  >
                    <option value="head_coach">Head coach</option>
                    <option value="coach">Coach</option>
                    <option value="club_owner">Club owner</option>
                    <option value="parent">Parent</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="font-label block mb-1">Message <span className="text-zinc-600">(optional)</span></label>
                <textarea
                  className="input"
                  rows={3}
                  value={form.message}
                  onChange={setField("message")}
                  placeholder="How many players / coaches? Anything specific you're hoping for?"
                  data-testid="demo-message"
                />
              </div>
              {err && (
                <div className="text-sm text-[#ff0026] bg-[#ff0026]/10 border border-[#ff0026]/30 rounded-md p-3" data-testid="demo-error">
                  {err}
                </div>
              )}
              <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="demo-submit">
                {busy ? "Sending…" : (<>Send request <ArrowRight size={16} weight="bold" /></>)}
              </button>
              <p className="text-[11px] text-zinc-600 text-center">
                We'll only use this to reply about courtsideOS. No marketing.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
